// Forked into ./redesign/Spotlight.tsx — keep in sync until phase_0 cleanup.
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { token } from '@bynder/design-system';
import { useRecentSearches } from './hooks/useRecentSearches';
import { initStickySearch } from './legacy/initStickySearch';
import { SearchByImageModal } from './SearchByImage/SearchByImageModal';
import { useSpotlightCapabilities } from './hooks/useSpotlightCapabilities';
import { getCollectionId } from './helpers/searchUtils';
import { getResponsiveSpotlightWidth, SEARCH_MIN_CHARS } from './constants';
import { ClearButton } from './IconsAndButtons/ClearButton';
import { LoadingIcon } from './IconsAndButtons/LoadingIcon';
import { SearchIcon } from './IconsAndButtons/SearchIcon';
import { useNLSSearch } from './NLSSearch/useNLSSearch';
import { KeyboardNavigationProvider } from './KeyboardNavigation';
import { SpotlightStateProvider, useSearchState } from './SpotlightState';
import { ResultsDropdown } from './ResultsDropdown/ResultsDropdown';
import { SearchInput } from './SearchBar/SearchInput';
import { SelectedSearchOptions } from './redesign/SelectedSearchOptions';
import { useSpotlightDropdown } from './hooks/useSpotlightDropdown';
import { useRunSearch } from './hooks/useRunSearch';
import { useSpotlightSearch } from './hooks/useSpotlightSearch';
import { useSpotlightClose } from './hooks/useSpotlightClose';

// The old fork (old Spotlight) has no search-by-image panel or result categories; we declare noop at module-level so
// the context values stay stable.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export function SpotlightOLD() {
	const refInput = useRef<HTMLInputElement>(null);
	const refContainer = useRef<HTMLDivElement>(null);

	const {
		searchTerm,
		isLoadingResults,
		searchResults,
		selectedSearchOptions,
		dispatch,
		setSearchTerm,
		setSelectedSearchOptions,
	} = useSearchState();

	const collectionId = getCollectionId(
		window.location.pathname,
		window.bynder.propsAssetOverview?.collectionId,
	);
	const { canShowStickySearch } = useSpotlightCapabilities();

	const { canUseSimilaritySearch, isNLSSearchOn, setIsNLSSearchOn, isPerformingNLSSearch } =
		useNLSSearch(canShowStickySearch);
	const { addSearchTermToRecentSearches, recentSearchTerms } = useRecentSearches({
		isNLSSearchOn: isPerformingNLSSearch,
	});

	const canShowSelectedSearchOptions = selectedSearchOptions.length > 0 && !isPerformingNLSSearch;

	const { isDropdownOpen, openSpotlightDropdown, closeSpotlightDropdown } = useSpotlightDropdown({
		refContainer,
	});

	const { cancelPendingSearch, onValueChange } = useSpotlightSearch({
		searchTerm,
		setSearchTerm,
		dispatch,
		collectionId,
		selectedSearchOptions,
		isDropdownSearchDisabled: isPerformingNLSSearch,
		isApplyingSimilarityFilter: isPerformingNLSSearch,
		isHybridSearchOn: false,
		openSpotlightDropdown,
	});

	useEffect(() => {
		if (canShowStickySearch) {
			const { searchTerm: stickySearchTerm, searchOptions } = initStickySearch();
			dispatch({
				type: 'stickySearchRestored',
				term: stickySearchTerm,
				options: searchOptions,
			});
		}
	}, [canShowStickySearch, dispatch]);

	const showLoadingIcon =
		isLoadingResults && !isPerformingNLSSearch && searchTerm.length >= SEARCH_MIN_CHARS;
	const showClearButton = searchTerm.length > 0 && (!isLoadingResults || isPerformingNLSSearch);

	const {
		clearResults,
		onClearButtonClick,
		removeSelectedSearchOption,
		closeAndClearSpotlight,
		onAfterSelect,
	} = useSpotlightClose({
		refInput,
		cancelPendingSearch,
		dispatch,
		openSpotlightDropdown,
		closeSpotlightDropdown,
		isApplyingSimilarityFilter: isPerformingNLSSearch,
		isHybridSearchOn: false,
		reopenOnRemoveOption: true,
	});

	const { defaultSearchAction } = useRunSearch({
		searchTerm,
		selectedSearchOptions,
		isApplyingSimilarityFilter: isPerformingNLSSearch,
		isHybridSearchOn: false,
		addSearchTermToRecentSearches,
		cleanUp: closeAndClearSpotlight,
	});

	const spotlightDropdown = {
		isDropdownOpen,
		openSpotlightDropdown,
		closeSpotlightDropdown,
		refInput,
		openSearchByImage: noop,
	};

	const search = {
		searchTerm,
		setSearchTerm,
		onValueChange,
		isLoadingResults,
		searchResults,
		clearResults,
		onClearButtonClick,
		defaultSearchAction,
		recentSearchTerms,
		addSearchTermToRecentSearches,
	};

	const scope = {
		selectedSearchOptions,
		setSelectedSearchOptions,
		removeSelectedSearchOption,
		selectedResultsCategory: null,
		setSelectedResultsCategory: noop,
	};

	const searchMode = {
		canUseSimilaritySearch,
		isNLSSearchOn,
		setIsNLSSearchOn,
		isPerformingNLSSearch,
		isHybridSearchEnabled: false,
		isHybridSearchOn: false,
		isDropdownSearchDisabled: isPerformingNLSSearch,
		isSearchOptionsDisabled: isPerformingNLSSearch,
	};

	return (
		<SpotlightStateProvider
			mode={searchMode}
			dropdown={spotlightDropdown}
			search={search}
			scope={scope}
		>
			<KeyboardNavigationProvider
				onEscape={closeSpotlightDropdown}
				onDefaultEnter={defaultSearchAction}
				onAfterSelect={onAfterSelect}
			>
				<StyledDiv ref={refContainer} className="nav-bar-search">
					<SearchIcon />

					<StyledInputWrapper>
						{canShowSelectedSearchOptions && <ClassicSelectedSearchOptions />}

						<SearchInput collectionId={collectionId} />
					</StyledInputWrapper>

					{showClearButton && <ClearButton onClick={onClearButtonClick} />}

					{showLoadingIcon && <LoadingIcon />}

					{canUseSimilaritySearch && <SearchByImageModal />}

					<ResultsDropdown
						showMainSection={isDropdownOpen && !isPerformingNLSSearch}
						showNLSBanner={canUseSimilaritySearch && isDropdownOpen}
					/>
				</StyledDiv>
			</KeyboardNavigationProvider>
		</SpotlightStateProvider>
	);
}

const StyledDiv = styled.div`
	${getResponsiveSpotlightWidth()}
`;

// The redesign's chips don't shrink, so they fit its search bar; here they may.
const ClassicSelectedSearchOptions = styled(SelectedSearchOptions)`
	flex-shrink: 1;
`;

const StyledInputWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${token.spacing3};
	width: 100%;
	height: 40px;
	padding: 0 15px 0 42px;
	border: 1px solid #d9dee1;
	border-radius: ${token.radiusMedium};
	background-color: ${token.white};
	box-sizing: border-box;
`;
